import type { Dayjs } from 'dayjs';
import { type ReactElement, useMemo, useState } from 'react';

import type ColorIndexService from './ColorIndexService.ts';
import RiskMetricsContribution from './RiskMetricsContribution.tsx';
import type { MetricInput } from './RiskMetricsChart.types.ts';
import { RiskMetricsChartInner } from './RiskMetricsChartInner.tsx';
import { RiskMetricsChartOptions } from './RiskMetricsChartOptions.tsx';
import type { ParameterDescription } from './RiskMetricsParameterService.tsx';
import {
  type IPortfolioMetricsQuery,
  type IPortfolioRiskMetricsInput,
  type IRiskDashboardInputQuery,
  type PortfolioMetricsQueryResult,
  usePortfolioMetricsQuery,
} from '../../../generated/graphql.tsx';
import { range7Days, rangeAll, rangeLastYear, rangeOneMonth, rangeThreeMonths } from '../../predefinedDateRanges.ts';
import PredefinedDateRangeButtonsWithCalendar from '../../technical/inputs/date/PredefinedDateRangeButtonsWithCalendar.tsx';
import ChartWithTitle from '../../ChartWithTitle.tsx';
import { calculateDefaultParameters } from './riskMetrics.utils.ts';
import { TwoThirdsLayout } from '../../TwoThirdsLayout.tsx';
import { convertDateRangeToSinceToDate } from 'components/dateRange.utils.ts';
import { Tooltip } from '@mui/joy';
import { tooltipEnterDelay } from 'theme/consts.ts';
import { WarmUpInput } from './WarmUpInput.tsx';
import {
  calculateAvailableContributionMetrics,
  calculatePorfolioMetricsQueryInput,
  filterByWarmUpPeriod,
  isWarmUpPeriodEnabled,
} from './riskMetrics.utils.ts';

const warmUpPeriodDefault = 10;

export type MetricsData = {
  label: Label;
  portfolioDefinition?:
    | {
        name: string;
      }
    | null
    | undefined;
  series: IPortfolioMetricsQuery['portfolio']['metrics'][number]['series'];
}[];

export const RiskMetricsChartWithOptions = (props: {
  groups: IRiskDashboardInputQuery['assets']['assetGroups'];
  metricParameters: Record<Label, ParameterDescription[]>;
  withContribution?: boolean;
  metrics: string[];
  title: string;
  categoryService: ColorIndexService;
  filters: Pick<IPortfolioRiskMetricsInput, 'portfolioDefinitionIds' | 'subAccountAssetFilter' | 'since' | 'to'>;
  showDatePicker: boolean;
  showPortfolio?: boolean;
}): ReactElement => {
  const defaultDateRange = rangeAll;
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(defaultDateRange.value);
  const [metricInput, setMetricInput] = useState<MetricInput>({
    metric: props.metrics[0],
    params: calculateDefaultParameters(props.metricParameters[props.metrics[0]] ?? []),
  });

  const [warmUpPeriod, setWarmUpPeriod] = useState<string>(String(warmUpPeriodDefault));
  const { skip, input } = calculatePorfolioMetricsQueryInput({
    metricInput,
    metricParamDescriptions: props.metricParameters[metricInput.metric] ?? [],
    filters: {
      ...props.filters,
      ...convertDateRangeToSinceToDate(dateRange),
    },
  });

  const queryOutput = usePortfolioMetricsQuery({
    variables: {
      input,
    },
    skip,
  });

  const warmUpPeriodEnabled = isWarmUpPeriodEnabled(queryOutput.data?.portfolio.metrics);

  return (
    <ChartWithTitle title={props.title} paper>
      <RiskMetricsChartOptions
        metricParameters={props.metricParameters}
        metricInput={metricInput}
        setMetricInput={setMetricInput}
        metrics={props.metrics}
        extraParams={
          <>
            <Tooltip
              enterDelay={tooltipEnterDelay.Slow}
              title="Excludes the first N days from risk analysis to remove expanding metric extreme values"
            >
              <WarmUpInput
                disabled={!warmUpPeriodEnabled}
                onChange={(val) => setWarmUpPeriod(val)}
                value={warmUpPeriod}
              />
            </Tooltip>
            {props.showDatePicker && (
              <PredefinedDateRangeButtonsWithCalendar
                defaultValue={defaultDateRange}
                onChange={(val): void => setDateRange(val)}
                // defaults without 1 day, metrics data doesn't make sense for 1 day
                calendarDateRanges={[range7Days, rangeOneMonth, rangeThreeMonths, rangeLastYear, rangeAll]}
                alignItems="end"
              />
            )}
          </>
        }
      />
      <RiskMetricsChart
        {...props}
        metricInput={metricInput}
        dateRange={dateRange}
        warmUpPeriod={warmUpPeriod}
        queryOutput={queryOutput}
      />
    </ChartWithTitle>
  );
};

export const RiskMetricsChart = (props: {
  fullHeight?: boolean;
  groups: IRiskDashboardInputQuery['assets']['assetGroups'];
  metricParameters: Record<Label, ParameterDescription[]>;
  withContribution?: boolean;
  title: string;
  categoryService: ColorIndexService;
  filters: Pick<IPortfolioRiskMetricsInput, 'portfolioDefinitionIds' | 'subAccountAssetFilter' | 'since' | 'to'>;
  showPortfolio?: boolean;
  dateRange: [Dayjs, Dayjs] | null;
  metricInput: MetricInput;
  warmUpPeriod: string;
  queryOutput: PortfolioMetricsQueryResult;
}): ReactElement => {
  const [date, setDate] = useState<UtcDate | undefined>(undefined);
  const availableContributionMetrics = useMemo(
    () => calculateAvailableContributionMetrics(props.queryOutput.data, date),
    [props.queryOutput.data, date]
  );

  const warmUpPeriodEnabled = isWarmUpPeriodEnabled(props.queryOutput.data?.portfolio.metrics);
  const metricsData = filterByWarmUpPeriod(
    props.warmUpPeriod,
    warmUpPeriodEnabled,
    props.queryOutput.data?.portfolio.metrics
  );

  const renderContribution = props.withContribution && availableContributionMetrics;
  const mainGraph = (
    <RiskMetricsChartInner
      fullHeight={props.fullHeight}
      {...props}
      error={props.queryOutput.error}
      metricInput={props.metricInput}
      loading={props.queryOutput.loading}
      metricsData={metricsData}
      onDateSelected={setDate}
    />
  );
  const chart = renderContribution ? (
    <TwoThirdsLayout
      fullHeight={props.fullHeight}
      left={mainGraph}
      right={
        <RiskMetricsContribution
          availableContributionMetrics={availableContributionMetrics}
          groups={props.groups.genieGroups}
          filters={props.filters}
          fullHeight={props.fullHeight}
          parameterDescription={new Map(Object.entries(props.metricParameters))}
        />
      }
    />
  ) : (
    mainGraph
  );

  return chart;
};
